// src/pages/LoginPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";
import { SERVER_BASE_URL } from "../config";

function LoginPage() {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError("");
    setResendSuccess(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResendSuccess(false);
    
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData),
      });
  
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to login");
      }
  
      const data = await response.json();
      const token = data.access_token;
  
      await login(token);
  
      const userResponse = await fetch(`${SERVER_BASE_URL}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!userResponse.ok) {
        throw new Error("Failed to fetch user data");
      }
  
      const userData = await userResponse.json();
  
      if (userData.role === "worker") {
        navigate("/worker-dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.username || resendLoading) return;
    
    setResendLoading(true);
    setResendSuccess(false);
    
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/users/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.username }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to resend verification email");
      }
      
      setResendSuccess(true);
    } catch (err) {
      console.error(err.message);
      // We won't show error here to prevent email enumeration
    } finally {
      setResendLoading(false);
    }
  };

  // Check if the error is about email verification
  const isEmailVerificationError = error.includes("Email is not verified");

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Welcome Back</h2>
          <p className="text-gray-600 mt-1">Sign in to your account</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <div className="relative">
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <a href="/reset-password" className="text-sm text-indigo-600 hover:text-indigo-800">
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    className="h-5 w-5 text-gray-500 hover:text-gray-700"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13.875 18.825C12.625 19.275 11.25 19.5 9.75 19.5c-6 0-9.75-4.5-9.75-9S3.75 1.5 9.75 1.5 19.5 6 19.5 10.5c0 1.5-.25 2.875-.7 4.125" 
                    />
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M16.5 17.25l3 3m0 0l3 3m-3-3h-6m6 0l-3-3" 
                    />
                  </svg>
                ) : (
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    className="h-5 w-5 text-gray-500 hover:text-gray-700"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                    />
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 text-center">{error}</p>
              
              {isEmailVerificationError && (
                <div className="mt-2 text-center">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="text-sm text-red-600 border border-red-300 bg-white hover:bg-red-50 px-3 py-1 rounded-md transition duration-200"
                    disabled={resendLoading}
                  >
                    {resendLoading ? (
                      <>
                        <Spinner className="inline w-3 h-3 mr-1" />
                        <span>Отправка...</span>
                      </>
                    ) : (
                      "Resend verification email"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {resendSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600 text-center">
                Verification email has been sent. Please check your inbox.
              </p>
            </div>
          )}

          <button
            type="submit"
            className={`w-full bg-indigo-600 text-white py-2 px-4 rounded-md font-medium flex items-center justify-center ${
              loading ? "opacity-70 cursor-not-allowed" : "hover:bg-indigo-700"
            } transition duration-200`}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner className="w-5 h-5 mr-2" />
                <span>Signing in...</span>
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;